import { CdkTrapFocus } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import type { ElementRef, SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  input,
  ViewChild,
  output,
} from '@angular/core';

import type { IPageInfo, VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';

import type { Attribute, AttributeListData } from '@core/http-types';

import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { IconComponent } from '../../common/icon/icon.component';
import { ProgressBarComponent } from '../../common/progress/progress-bar/progress-bar.component';
import { provideIntersectionObserverArea } from '../../utils/intersection-observer/intersection-observer-area';

export interface AttributeVisibilityChange {
  isVisible: boolean;
  attribute: Attribute;
  index: number;
}

@Component({
  selector: 'rt-attributes-select-list',
  styleUrls: ['./attributes-select-list.component.less'],
  template: `
    <ng-template #item let-attr>
      <rt-dropdown-item
        [highlighted]="highlightedId() === attr.id"
        (click)="saveAttribute.emit({ value: attr.value, id: attr.id })"
      >
        {{ attr.id !== createNewItemId() ? attr.value : null }}
        @if (attr.id === createNewItemId()) {
          <ng-container [ngTemplateOutlet]="createNewAttr()" />
        }
        <span suffix class="counter">
          {{ attr.count }}
          @if (markSelected() && selectedMap()[attr.id]) {
            <rt-icon name="check" size="small" colorToken="colorIconSuccessDefault" />
          }
        </span>
      </rt-dropdown-item>
    </ng-template>

    <ng-template #noVirtualScrollTmpl>
      @for (attr of viewportItems; track trackByAttribute(i, attr); let i = $index) {
        <ng-container [ngTemplateOutlet]="item" [ngTemplateOutletContext]="{ $implicit: attr }" />
      }
    </ng-template>

    <ng-template #virtualScrollTmpl>
      <div
        #scroll
        virtualScroller
        [bufferAmount]="5"
        [scrollAnimationTime]="0"
        [scrollThrottlingTime]="10"
        [enableUnequalChildrenSizes]="true"
        [executeRefreshOutsideAngularZone]="true"
        [items]="suggestionsListData().rows"
        (vsUpdate)="onVirtualScrollUpdate($event)"
        (vsChange)="onVirtualScrollChange($event)"
        [style.height]="maxHeight()"
        class="scroll"
      >
        <div #container>
          <ng-container [ngTemplateOutlet]="noVirtualScrollTmpl" />
        </div>
        @if (isLoading) {
          <rt-progress-bar />
        }
      </div>
    </ng-template>

    <div #scrollContainer cdkTrapFocus [style.max-height]="maxHeight()">
      <ng-container
        [ngTemplateOutlet]="virtualScrollEnabled ? virtualScrollTmpl : noVirtualScrollTmpl"
      />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...provideIntersectionObserverArea()],
  imports: [
    DropdownItemComponent,
    NgTemplateOutlet,
    ProgressBarComponent,
    VirtualScrollerModule,
    CdkTrapFocus,
    IconComponent,
  ],
})
export class AttributesSelectListComponent {
  private readonly REQUEST_NEW_DATA = 20;

  public readonly suggestionsListData = input<AttributeListData>(undefined!);
  public readonly highlightedId = input<string | number | null>(undefined!);
  public readonly createNewItemId = input<string>(undefined!);
  public readonly createNewAttr = input<TemplateRef<any>>(undefined!);
  public readonly maxHeight = input<string>('300px');
  public readonly markSelected = input(false);

  public selected = input<Attribute[]>([]);

  public viewportItems: AttributeListData['rows'] = [];
  public isLoading: boolean = true;
  public virtualScrollEnabled: boolean;

  public saveAttribute = output<Attribute>();
  public loadMore = output<void>();

  public selectedMap = computed(() =>
    Object.fromEntries(this.selected().map(attr => [attr.id, true])),
  );

  @ViewChild('scroll', { static: false }) private virtualScroll: VirtualScrollerComponent | null;
  @ViewChild('scrollContainer', { static: true }) private scrollContainer: ElementRef;

  private currentPage: number = 0;
  private canRequestData: boolean = true;

  constructor(private cdRef: ChangeDetectorRef) {}

  public onVirtualScrollUpdate(visibleItems: AttributeListData['rows']): void {
    this.viewportItems = visibleItems;
    this.cdRef.detectChanges();
  }

  public onVirtualScrollChange(event: IPageInfo): void {
    this.onVisibilityChange({
      attribute: this.viewportItems[event.endIndex],
      isVisible: true,
      index: event.endIndex,
    });
  }

  public trackByAttribute(index: number, attr: Attribute): string {
    return attr.value;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.virtualScrollEnabled = this.suggestionsListData().meta!.totalCount > 20;
    if (!this.virtualScrollEnabled) {
      this.viewportItems = this.suggestionsListData().rows;
    }

    if ('highlightedId' in changes) {
      if (this.virtualScroll) {
        this.virtualScroll.scrollToIndex(0);
      } else {
        this.scrollContainer.nativeElement.scrollTop = 0;
      }
    }
    if ('suggestionsListData' in changes) {
      const { page } = this.suggestionsListData().meta!;
      if (this.currentPage !== page) {
        this.currentPage = page;
        this.isLoading = false;
        this.canRequestData = true;
      }
    }
  }

  public onVisibilityChange(event: AttributeVisibilityChange) {
    const { page, limit, totalCount } = this.suggestionsListData().meta!;
    const leftToSeeCount = this.suggestionsListData().rows.length - event.index;

    const allDataLoaded = Math.ceil(totalCount / limit) === page;
    const shouldRequestData =
      event.isVisible &&
      this.canRequestData &&
      !allDataLoaded &&
      leftToSeeCount < this.REQUEST_NEW_DATA;

    if (shouldRequestData) {
      this.canRequestData = false;
      this.loadMore.emit();
      this.isLoading = true;
    }
  }
}
